// 🎃 10-07 pumpkins (P7): THE CARVING TABLE, the 09-28 mockup page as a full sheet (Nina: "Pumpkin feel is fine";
// frozen copy https://claude.ai/artifact/44e8LEB484m5isg1ZaobV8): the table (carve with a mouse or a finger) + the
// porch tonight + the big view (replay the carving; Hide for the house's maintainer seats).
// Rulings: R22 a PAINTED pumpkin under the cuts (utils/pumpkinDraw) · R23 imports carve their own · the porch legend
// counts every House AI (not "residents carved their own") · a plain "Carve" chip opens this sheet.
// A carve costs no AI and never wakes the room: utils/pumpkinApi posts it directly and the page starts no step.
// ⚠️ Class names carry no theme substring (card · panel · button · btn · tag · chip · input · primary · header):
// the House theme styles by substring. Player copy: short, no em dashes.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CANVAS, KNIFE_FINE, KNIFE_SCOOP, MIN_STEP, LIVE_MAX, NAME_MAX, STENCIL_BUTTONS,
  cutsForStroke, stencilCuts, carvePayload, roomFor, COPY,
} from '../../utils/pumpkinCarve';
import { drawPumpkin, loadPainting } from '../../utils/pumpkinDraw';
import { submitPumpkin, hidePumpkin, fetchPorch } from '../../utils/pumpkinApi';
import PorchPumpkins, { placePumpkins, undrawnPumpkins } from './PorchPumpkins';
import { emitGameEvent, GAME_EVENTS } from '../../utils/gameEventBus';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Grandstander:wght@600;800&family=Atkinson+Hyperlegible:wght@400;700&display=swap';
export function ensurePumpkinFonts(doc = (typeof document !== 'undefined' ? document : null)) {
  try {
    if (!doc || doc.querySelector('link[data-pumpkin-fonts]')) return;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    link.setAttribute('data-pumpkin-fonts', '');
    doc.head.appendChild(link);
  } catch (e) { /* the system fonts still read */ }
}

export const PLACED_TOAST = 'On the porch. It glows every night until Halloween.';
export const KEPT_HIDDEN_TOAST = 'Saved. It stays off the porch for now.'; // a re-carve keeps the house's hide
/** A name clamped the backend's way: NAME_MAX code points (maxLength counts UTF-16 units and can split an emoji). */
export const clampName = (v) => Array.from(String(v == null ? '' : v)).slice(0, NAME_MAX).join('');
/** "More" in See all ADDS the next page under the ones already shown (it replaced them). */
export function mergePage(prev, next) {
  if (!next) return prev || null;
  const items = Array.isArray(next.items) ? next.items : [];
  if (prev && Array.isArray(prev.items) && Number(next.n) === Number(prev.n) + 1) {
    const seen = new Set(prev.items.map((pk) => pk && pk.id));
    return { ...next, items: prev.items.concat(items.filter((pk) => pk && !seen.has(pk.id))) };
  }
  return { ...next, items };
}
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
export const HIDDEN_TOAST = 'Hidden from the porch.';
export const UNHIDDEN_TOAST = 'Back on the porch.';
const HINT = 'Trace a shape all the way around to pop it out. An open line just scores the skin.';
const HISTORY_MAX = 60;
const REPLAY_MS = 3200;

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};
/** "Sat, Oct 24" on the house clock (Phoenix): the night is the house's, not the viewer's. */
export function houseDate(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { timeZone: 'America/Phoenix', weekday: 'short', month: 'short', day: 'numeric' });
}
/** The header's first line, from the rider (never a hard-coded date). */
export function openLine(rider, now = Date.now()) {
  if (!rider) return '';
  if (rider.open) return 'Open now, and on the porch until Halloween.';
  const at = Date.parse(rider.opens_at);
  if (Number.isFinite(at) && at > now) return `Opens at the pumpkin patch on ${houseDate(rider.opens_at)} and stays on the porch until Halloween.`;
  return 'The carving table is packed away until next fall.';
}

const CSS = `
.pk-sheet{position:fixed;inset:0;z-index:5200;overflow-y:auto;-webkit-overflow-scrolling:touch;color-scheme:dark;
  background:radial-gradient(1200px 600px at 50% -10%,#2a2048 0%,#120f1f 60%) fixed,#120f1f;color:#f4ecdb;
  font:16px/1.5 "Atkinson Hyperlegible",system-ui,-apple-system,"Segoe UI",sans-serif}
.pk-sheet *{box-sizing:border-box}
.pk-wrap{max-width:1180px;margin:0 auto;padding:28px 18px calc(64px + env(safe-area-inset-bottom,0px));display:grid;gap:34px}
.pk-h1,.pk-h2{font-family:"Grandstander","Trebuchet MS",system-ui,sans-serif;font-weight:800;margin:0;letter-spacing:.005em;text-shadow:none}
.pk-h1{font-size:clamp(30px,5.4vw,48px);line-height:1.05}.pk-h1 span{color:#f08a24}
.pk-h2{font-size:clamp(21px,3vw,27px);line-height:1.15}
.pk-eyebrow{font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#aaa1c2}
.pk-top{display:grid;gap:10px;max-width:70ch;padding-right:52px}.pk-top p{margin:0;color:#aaa1c2}.pk-top strong{color:#f4ecdb}
.pk-table{display:grid;grid-template-columns:minmax(0,560px) minmax(0,1fr);gap:26px;align-items:start}
.pk-slab{position:relative;width:100%;max-width:min(560px,calc(100vh - 32px));max-width:min(560px,calc(100dvh - 32px))} /* a short screen (a phone held sideways) still shows the whole pumpkin */
.pk-canvas{display:block;width:100%;aspect-ratio:1/1;border-radius:22px;touch-action:none;cursor:crosshair;
  box-shadow:0 30px 60px -30px rgba(0,0,0,.8),0 0 0 1px #342c52;background:#2a1c12}
.pk-hint{margin:10px 2px 0;font-size:14px;color:#aaa1c2}
.pk-tools{display:grid;gap:18px}.pk-group{display:grid;gap:8px}
.pk-label{font-size:12px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#aaa1c2}
.pk-row{display:flex;flex-wrap:wrap;gap:8px}
.pk-sheet .pk-b{font:inherit;font-size:15px;font-weight:700;color:#f4ecdb;background:#241d3c;border:1px solid #342c52;border-radius:12px;
  padding:9px 14px;cursor:pointer;text-shadow:none;position:static;overflow:visible;transform:none;box-shadow:none;touch-action:manipulation}
.pk-sheet .pk-b:hover{border-color:#56497f;transform:none;box-shadow:none}.pk-sheet .pk-b:active{transform:none;box-shadow:none}
.pk-sheet .pk-b:focus-visible,.pk-field:focus-visible{outline:2px solid #ffd76a;outline-offset:2px}
.pk-sheet .pk-b[aria-pressed="true"]{background:#3a2c14;border-color:#f08a24;color:#ffe2b0}
.pk-sheet .pk-go{background:linear-gradient(180deg,#ffa640,#e27413);border-color:#ffb660;color:#2a1405}
.pk-sheet .pk-go:disabled{opacity:.45;cursor:not-allowed}
.pk-sheet .pk-quiet{background:transparent}
.pk-sheet .pk-hide{color:#ff8a7a;border-color:#6b3a3a}
.pk-field{font:inherit;font-size:16px;color:#f4ecdb;background:#1c1730;border:1px solid #342c52;border-radius:12px;padding:10px 12px;width:100%;max-width:320px}
.pk-note{font-size:14px;color:#aaa1c2;margin:0;max-width:62ch}.pk-note b{color:#f4ecdb}
.pk-say{font-size:14px;color:#ffd0a8;margin:0}
.pk-porch{display:grid;gap:12px}
.pk-porch-head{display:flex;flex-wrap:wrap;align-items:baseline;justify-content:space-between;gap:8px 16px}
.pk-scene{position:relative;margin:0;border-radius:14px;overflow:hidden;box-shadow:0 0 0 1px #342c52,0 24px 50px -28px rgba(0,0,0,.9);background:#0b0814}
.pk-scene img{display:block;width:100%;height:auto;aspect-ratio:1536/672}
.pk-legend{display:flex;flex-wrap:wrap;gap:14px;font-size:13px;color:#aaa1c2}
.pk-dot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:6px;vertical-align:1px}
.pk-list{display:grid;gap:6px;margin:0;padding:0;list-style:none}
.pk-list li{display:flex;gap:10px;align-items:center;justify-content:space-between;background:#1c1730;border:1px solid #342c52;border-radius:12px;padding:8px 12px}
.pk-sheet .pk-b.pk-close{position:fixed;top:calc(12px + env(safe-area-inset-top,0px));right:14px;z-index:5201;background:#241d3c}
.pk-veil{position:fixed;inset:0;z-index:5210;background:rgba(6,4,14,.72);display:flex;align-items:center;justify-content:center;padding:16px}
.pk-dlg{border:1px solid #342c52;border-radius:20px;background:#1c1730;color:#f4ecdb;padding:18px;width:min(440px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow-y:auto}
.pk-big{display:block;width:100%;aspect-ratio:1/1;border-radius:16px;background:#0b0814}
.pk-dlg-name{font-family:"Grandstander",system-ui,sans-serif;font-weight:800;font-size:22px}
.pk-dlg-by{color:#aaa1c2;font-size:14px}
.pk-toast{position:fixed;left:50%;bottom:calc(22px + env(safe-area-inset-bottom,0px));transform:translateX(-50%);z-index:5220;
  background:#2e2410;color:#ffe2b0;border:1px solid #7a5a22;border-radius:999px;padding:9px 16px;font-weight:700;font-size:14px;max-width:calc(100vw - 32px);text-align:center}
@media (max-width:880px){.pk-table{grid-template-columns:1fr}.pk-slab{margin:0 auto}}
`;

/**
 * The sheet. Props: rider (house.pumpkins) · porch (the porch read) + onRefresh · gameId / playerId / playerSecret
 * (the viewer's proven seat) · porchPicture (the porch take the sprites sit on) · onClose.
 */
export default function PumpkinCarving({ rider, porch, onRefresh, gameId, playerId, playerSecret, porchPicture, onClose }) {
  const mine = porch && porch.mine ? porch.mine : null;
  const [cuts, setCuts] = useState(() => (mine && Array.isArray(mine.cuts) && mine.cuts.length ? mine.cuts : stencilCuts('classic')));
  const [name, setName] = useState(() => (mine && mine.name && mine.name !== 'Unnamed' ? mine.name : ''));
  const [knife, setKnife] = useState(KNIFE_FINE);
  const [mirror, setMirror] = useState(true);
  const [lit, setLit] = useState(false);
  const [say, setSay] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState('');
  const [clearArmed, setClearArmed] = useState(false);
  const [big, setBig] = useState(null); // { pk, mine }
  const [allOpen, setAllOpen] = useState(false);
  const [page, setPage] = useState(null); // { n, pages, items }
  const history = useRef([]);
  const live = useRef(null);
  const canvasRef = useRef(null);
  const bigRef = useRef(null);
  const porchRef = useRef(null);
  const seededFrom = useRef(mine ? mine.id : null);
  const sheetRef = useRef(null);
  const bigCloseRef = useRef(null);
  const pointers = useRef(new Set()); // every pointer down on the table (two = a pinch, never a cut)
  const liveId = useRef(null);        // the one pointer the live stroke follows
  // the rider says open; a porch read that says closed wins (a slow or failed read never blocks the knife)
  const open = !!(rider && rider.open) && !(porch && porch.open === false);
  const closeRef = useRef(null);

  useEffect(() => {
    ensurePumpkinFonts();
    const back = typeof document !== 'undefined' ? document.activeElement : null; // focus goes back here on close
    try { if (closeRef.current) closeRef.current.focus(); } catch (e) { /* no focus is fine */ }
    // the app's ♫ button (LofiRadio, fixed top right, z 100010 on phones) sat on this sheet's ✕: it steps out while
    // the sheet is open (its own event, the ported place's and the room frame's idiom); the music plays on
    emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: true });
    return () => {
      emitGameEvent(GAME_EVENTS.MUSIC_FAB_HIDDEN, { hidden: false });
      try { if (back && typeof back.focus === 'function' && back.isConnected) back.focus(); } catch (e) { /* fine */ }
    };
  }, []);
  // a pumpkin that arrives AFTER the sheet opened (the porch read was slow) seeds the table once, if untouched
  useEffect(() => {
    if (!mine || seededFrom.current === mine.id || history.current.length) return;
    seededFrom.current = mine.id;
    if (Array.isArray(mine.cuts) && mine.cuts.length) setCuts(mine.cuts);
    if (mine.name && mine.name !== 'Unnamed') setName(mine.name);
  }, [mine]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const t = e.target;
      // Escape in the name field lets go of the field, never the carving
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) { try { t.blur(); } catch (x) { /* fine */ } return; }
      if (big) setBig(null); else if (onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big, onClose]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!clearArmed) return undefined;
    const t = setTimeout(() => setClearArmed(false), 2500);
    return () => clearTimeout(t);
  }, [clearArmed]);

  // ── the table ──
  const draw = useCallback((flick = 1) => {
    const c = canvasRef.current;
    if (!c) return;
    const pts = live.current;
    const extra = pts && pts.length >= 2
      ? (mirror ? [{ k: 'line', w: knife, p: pts }, { k: 'line', w: knife, p: pts.map(([x, y]) => [CANVAS - x, y]) }] : [{ k: 'line', w: knife, p: pts }])
      : [];
    drawPumpkin(c, { cuts: extra.length ? cuts.concat(extra) : cuts }, { backdrop: true, lit, flick, size: c.clientWidth || 400 });
  }, [cuts, lit, knife, mirror]);
  useEffect(() => loadPainting(() => draw()), [draw]);
  useEffect(() => {
    draw();
    if (!lit || reducedMotion()) return undefined;
    let raf = 0;
    let last = 0;
    // ~16 fps: a candle, not a video (every frame redrew the whole table with a blur on every cut, hard on a phone)
    const loop = (t) => { if (t - last >= 60) { draw(0.86 + 0.14 * Math.sin(t / 90) * Math.sin(t / 37 + 1)); last = t; } raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [draw, lit]);
  useEffect(() => {
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [draw]);

  const remember = () => { history.current.push(cuts); if (history.current.length > HISTORY_MAX) history.current.shift(); };
  const toLogical = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return [Math.max(0, Math.min(CANVAS, ((e.clientX - r.left) / r.width) * CANVAS)), Math.max(0, Math.min(CANVAS, ((e.clientY - r.top) / r.height) * CANVAS))];
  };
  const onDown = (e) => {
    if (e.isPrimary !== false) pointers.current = new Set(); // a new gesture: nothing else is down (heals a lost up)
    pointers.current.add(e.pointerId);
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (x) { /* an old browser still carves */ }
    if (pointers.current.size > 1) { // a second finger: a pinch is never a cut (the live stroke goes too)
      live.current = null;
      liveId.current = null;
      draw();
      return;
    }
    if (!open) return;
    e.preventDefault();
    liveId.current = e.pointerId;
    live.current = [toLogical(e)];
    setSay('');
    draw();
  };
  const onMove = (e) => {
    const pts = live.current;
    if (!pts || e.pointerId !== liveId.current) return;
    const p = toLogical(e);
    const q = pts[pts.length - 1];
    if (Math.hypot(p[0] - q[0], p[1] - q[1]) >= MIN_STEP && pts.length < LIVE_MAX) { pts.push(p); draw(); }
  };
  const onCancel = (e) => {
    pointers.current.delete(e.pointerId);
    if (e.pointerId !== liveId.current) return;
    liveId.current = null;
    live.current = null;
    draw();
  };
  const onUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (e.pointerId !== liveId.current) return;
    liveId.current = null;
    const pts = live.current;
    live.current = null;
    if (!pts) return;
    const added = cutsForStroke(pts, { knife, mirror });
    if (!added.length) { draw(); return; }
    if (!roomFor(cuts, added.length)) { setSay(COPY.too_many); draw(); return; }
    remember();
    setCuts(cuts.concat(added));
  };
  const addStencil = (id) => {
    const add = stencilCuts(id);
    if (!roomFor(cuts, add.length)) { setSay(COPY.too_many); return; }
    remember();
    setSay('');
    setCuts(cuts.concat(add));
  };
  const undo = () => { if (history.current.length) setCuts(history.current.pop()); setSay(''); };
  const clear = () => {
    if (!cuts.length) return;
    if (!clearArmed) { setClearArmed(true); return; }
    setClearArmed(false);
    remember();
    setCuts([]);
  };

  // ── put it on the porch ──
  const place = async () => {
    if (sending || !open) return;
    const out = carvePayload({ name, cuts });
    if (!out.ok) { setSay(out.message); return; }
    setSending(true);
    setSay('');
    try {
      const answer = await submitPumpkin({ gameId, playerId, playerSecret, name: out.body.name, cuts: out.body.cuts });
      if (answer && answer.ok) {
        setToast(answer.hidden ? KEPT_HIDDEN_TOAST : PLACED_TOAST);
        if (onRefresh) await onRefresh();
        try { porchRef.current.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' }); } catch (e) { /* fine where it is */ }
      } else {
        setSay((answer && answer.message) || COPY.bad);
      }
    } catch (e) {
      setSay((e && e.message) || COPY.bad);
    } finally {
      setSending(false);
    }
  };

  // ── the big view ──
  useEffect(() => {
    if (!big) return undefined;
    const c = bigRef.current;
    const paint = (progress) => drawPumpkin(c, big.pk, { lit: true, backdrop: true, progress, size: (c && c.clientWidth) || 400 });
    paint(null);
    try { if (bigCloseRef.current) bigCloseRef.current.focus(); } catch (e) { /* fine */ }
    return loadPainting(() => paint(null));
  }, [big]);
  const replay = () => {
    if (!big) return;
    const c = bigRef.current;
    if (reducedMotion()) { drawPumpkin(c, big.pk, { lit: true, backdrop: true, size: (c && c.clientWidth) || 400 }); return; }
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / REPLAY_MS);
      drawPumpkin(c, big.pk, { lit: true, backdrop: true, progress: p, flick: 1, size: (c && c.clientWidth) || 400 });
      if (p < 1 && bigRef.current === c) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const setHidden = async (pk, hidden) => {
    try {
      const answer = await hidePumpkin({ gameId, playerId, playerSecret, pumpkinId: pk.id, hidden });
      if (answer && answer.ok === false) { setToast(answer.message || COPY.bad); return; }
      setBig(null);
      setToast(hidden ? HIDDEN_TOAST : UNHIDDEN_TOAST);
      if (onRefresh) await onRefresh();
    } catch (e) { setToast((e && e.message) || COPY.bad); }
  };

  // ── see all ──
  const placed = useMemo(() => placePumpkins(porch), [porch]);
  const undrawn = useMemo(() => undrawnPumpkins(porch, placed), [porch, placed]);
  const restCount = undrawn.length + Math.max(0, Number(porch && porch.rest) || 0);
  const loadPage = async (n) => {
    try {
      const got = await fetchPorch({ gameId, playerId, playerSecret, page: n });
      if (got && got.page) setPage((prev) => mergePage(prev, got.page));
    } catch (e) { setToast((e && e.message) || COPY.bad); }
  };

  const by = (pk, isMine) => (isMine ? 'carved by you' : `carved by ${pk.by_name || 'someone'}${pk.kind === 'resident' ? ' · House AI' : ''}`);
  // Tab stays inside the sheet (a modal over the page)
  const onTrap = (e) => {
    if (e.key !== 'Tab' || !sheetRef.current) return;
    const f = Array.from(sheetRef.current.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  return (
    <div className="pk-sheet" role="dialog" aria-modal="true" aria-label="The carving table" data-pumpkin-sheet="1" ref={sheetRef} onKeyDown={onTrap}>
      <style>{CSS}</style>
      <button ref={closeRef} type="button" className="pk-b pk-quiet pk-close" onClick={onClose} aria-label="Back to the house">✕</button>
      <div className="pk-wrap">
        <div className="pk-top">
          <span className="pk-eyebrow">Halloween · the carving table</span>
          <h1 className="pk-h1">Carve a <span>pumpkin</span></h1>
          <p><strong>{openLine(rider)}</strong> Carve with your mouse or finger. Your pumpkin sits on the porch and glows every night, and on Oct 31 the whole porch is lit.</p>
        </div>

        <section className="pk-table" aria-labelledby="pk-carve-title">
          <div className="pk-slab">
            <canvas
              ref={canvasRef}
              className="pk-canvas"
              width={CANVAS}
              height={CANVAS}
              aria-label="Your pumpkin. Drag to carve."
              data-pumpkin-table="1"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onCancel}
            />
            <p className="pk-hint">{HINT}</p>
          </div>
          <div className="pk-tools">
            <h2 className="pk-h2" id="pk-carve-title">Your pumpkin</h2>
            <div className="pk-group">
              <span className="pk-label">Knife</span>
              <div className="pk-row" role="group" aria-label="Knife size">
                <button type="button" className="pk-b" aria-pressed={knife === KNIFE_FINE} onClick={() => setKnife(KNIFE_FINE)}>Fine</button>
                <button type="button" className="pk-b" aria-pressed={knife === KNIFE_SCOOP} onClick={() => setKnife(KNIFE_SCOOP)}>Big scoop</button>
                <button type="button" className="pk-b" aria-pressed={mirror} title="Carve one side, get both" onClick={() => setMirror((m) => !m)}>Mirror</button>
              </div>
            </div>
            <div className="pk-group">
              <span className="pk-label">Stencils</span>
              <div className="pk-row" role="group" aria-label="Stencils">
                {STENCIL_BUTTONS.map(([id, label]) => (
                  <button key={id} type="button" className="pk-b" data-stencil={id} onClick={() => addStencil(id)} disabled={!open}>{label}</button>
                ))}
              </div>
            </div>
            <div className="pk-group">
              <span className="pk-label">Light</span>
              <div className="pk-row">
                <button type="button" className="pk-b" aria-pressed={lit} onClick={() => setLit((l) => !l)}>🕯️ Light it</button>
                <button type="button" className="pk-b pk-quiet" onClick={undo}>Undo</button>
                <button type="button" className="pk-b pk-quiet" onClick={clear}>{clearArmed ? 'Sure? Clear' : 'Clear'}</button>
              </div>
            </div>
            <div className="pk-group">
              <label className="pk-label" htmlFor="pk-name">Name it</label>
              <input id="pk-name" type="text" className="pk-field" maxLength={NAME_MAX * 2} placeholder="Gourdon, probably" autoComplete="off"
                value={name} onChange={(e) => setName(clampName(e.target.value))} />
            </div>
            <div className="pk-row">
              <button type="button" className="pk-b pk-go" data-pumpkin-place="1" onClick={place} disabled={!cuts.length || sending || !open}>
                {sending ? 'Putting it out…' : (mine ? 'Put the new one out' : 'Put it on the porch')}
              </button>
            </div>
            {say && <p className="pk-say" role="status" data-pumpkin-say="1">{say}</p>}
            {mine && mine.hidden && <p className="pk-say" role="status">The house took yours off the porch.</p>}
            <p className="pk-note"><b>One pumpkin each.</b> Re-carve it any time until Halloween. It goes on the porch right away, and the house can take one down. Carving costs no AI.</p>
          </div>
        </section>

        <section className="pk-porch" aria-labelledby="pk-porch-title" ref={porchRef}>
          <div className="pk-porch-head">
            <h2 className="pk-h2" id="pk-porch-title">{porch && porch.glow && porch.glow !== 'off' ? 'The porch tonight' : 'The porch'}</h2>
            <div className="pk-legend">
              <span><i className="pk-dot" style={{ background: '#f5c451' }} />visitors</span>
              <span><i className="pk-dot" style={{ background: '#62d8ea' }} />House AIs</span>
            </div>
          </div>
          <figure className="pk-scene">
            {porchPicture ? <img src={porchPicture} alt="The house porch, with carved pumpkins glowing along the steps, the rail and the table." /> : <div style={{ aspectRatio: '1536 / 672' }} />}
            <PorchPumpkins porch={porch} onOpen={(pk, isMine) => setBig({ pk, mine: isMine })} />
          </figure>
          <p className="pk-note">{placed.length ? 'Tap a pumpkin to see it big and watch it get carved.' : 'Nothing on the porch yet. Yours could be the first.'}</p>
          {restCount > 0 && (
            <div className="pk-group">
              <button type="button" className="pk-b pk-quiet" aria-expanded={allOpen} onClick={() => setAllOpen((v) => !v)} data-pumpkin-all="1">
                {allOpen ? 'Hide the rest' : `See all ${restCount + placed.length}`}
              </button>
              {allOpen && (
                <ul className="pk-list">
                  {undrawn.concat(page ? page.items : []).map((pk) => (
                    <li key={pk.id}>
                      <span>{pk.name}<span className="pk-dlg-by"> · {by(pk, false)}</span></span>
                      <button type="button" className="pk-b pk-quiet" onClick={() => setBig({ pk, mine: false })}>See it</button>
                    </li>
                  ))}
                  {Number(porch && porch.rest) > 0 && (!page || page.n < page.pages) && (
                    <li><button type="button" className="pk-b pk-quiet" onClick={() => loadPage(page ? page.n + 1 : 1)}>More</button></li>
                  )}
                </ul>
              )}
            </div>
          )}
          {porch && porch.can_hide && Array.isArray(porch.hidden) && porch.hidden.length > 0 && (
            <div className="pk-group" data-pumpkin-hidden-list="1">
              <span className="pk-label">Taken down</span>
              <ul className="pk-list">
                {porch.hidden.map((pk) => (
                  <li key={pk.id}>
                    <span>{pk.name}<span className="pk-dlg-by"> · {by(pk, false)}</span></span>
                    <button type="button" className="pk-b pk-quiet" onClick={() => setHidden(pk, false)}>Put it back</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>

      {big && (
        <div className="pk-veil" onClick={(e) => { if (e.target === e.currentTarget) setBig(null); }}>
          <div className="pk-dlg" role="dialog" aria-modal="true" aria-labelledby="pk-dlg-name" data-pumpkin-big="1">
            <canvas ref={bigRef} className="pk-big" width={CANVAS} height={CANVAS} aria-hidden="true" />
            <div style={{ display: 'grid', gap: 2, margin: '12px 0' }}>
              <span className="pk-dlg-name" id="pk-dlg-name">{big.pk.name}</span>
              <span className="pk-dlg-by">{by(big.pk, big.mine)}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between' }}>
              <button type="button" className="pk-b" onClick={replay}>▶ Replay the carving</button>
              <div className="pk-row">
                {porch && porch.can_hide && !big.mine && (
                  <button type="button" className="pk-b pk-hide" data-pumpkin-hide="1" title="Only the house sees this" onClick={() => setHidden(big.pk, true)}>Hide</button>
                )}
                <button ref={bigCloseRef} type="button" className="pk-b pk-quiet" onClick={() => setBig(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="pk-toast" role="status" data-pumpkin-toast="1">{toast}</div>}
    </div>
  );
}
